import { db } from '../config/firebase';
import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, QueryDocumentSnapshot } from 'firebase/firestore';
import type { Payment } from '../types/index';

const PAYMENTS_REF = 'Payments';

// Check if Firebase is available
const isFirebaseAvailable = () => {
  try {
    return !!db;
  } catch (error) {
    console.error('Firebase not available:', error);
    return false;
  }
};


export const paymentService = {
  // Get all payments
  getAllPayments: async (): Promise<Payment[]> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning empty array');
      return [];
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const q = query(paymentsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const payments: Payment[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        } as Payment);
      });
      
      return payments;
    } catch (error) {
      console.error('Error fetching payments:', error);
      return [];
    }
  },

  // Get payment by ID
  getPaymentById: async (paymentId: string): Promise<Payment | null> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning null');
      return null;
    }

    try {
      const paymentRef = doc(db, PAYMENTS_REF, paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (paymentDoc.exists()) {
        return {
          id: paymentDoc.id,
          ...paymentDoc.data()
        } as Payment;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching payment:', error);
      return null;
    }
  },

  // Get payments by status
  getPaymentsByStatus: async (status: 'completed' | 'pending' | 'failed' | 'cancelled'): Promise<Payment[]> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning empty array');
      return [];
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const q = query(paymentsRef, where('status', '==', status), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const payments: Payment[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        } as Payment);
      });
      
      return payments;
    } catch (error) {
      console.error('Error fetching payments by status:', error);
      return [];
    }
  },

  // Get payments by client
  getPaymentsByClient: async (clientId: string): Promise<Payment[]> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning empty array');
      return [];
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const q = query(paymentsRef, where('clientID', '==', clientId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const payments: Payment[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        } as Payment);
      });
      
      return payments;
    } catch (error) {
      console.error('Error fetching payments by client:', error);
      return [];
    }
  },

  // Get payments by legal file
  getPaymentsByLegalFile: async (legalFileId: string): Promise<Payment[]> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning empty array');
      return [];
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const q = query(paymentsRef, where('legalFileId', '==', legalFileId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const payments: Payment[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        } as Payment);
      });
      
      return payments;
    } catch (error) {
      console.error('Error fetching payments by legal file:', error);
      return [];
    }
  },

  // Get active payments
  getActivePayments: async (): Promise<Payment[]> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, returning empty array');
      return [];
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const q = query(paymentsRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const payments: Payment[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        payments.push({
          id: doc.id,
          ...doc.data()
        } as Payment);
      });
      
      return payments;
    } catch (error) {
      console.error('Error fetching active payments:', error);
      return [];
    }
  },

  // Add new payment
  addPayment: async (paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Payment> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, cannot add payment');
      throw new Error('Firebase not available');
    }

    try {
      const paymentsRef = collection(db, PAYMENTS_REF);
      const timestamp = Date.now().toString();
      
      const newPaymentData = {
        ...paymentData,
        createdAt: timestamp,
        updatedAt: timestamp
      };
      
      const docRef = await addDoc(paymentsRef, newPaymentData);
      
      return {
        id: docRef.id,
        ...newPaymentData
      } as Payment;
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  },

  // Update payment
  updatePayment: async (paymentId: string, updates: Partial<Omit<Payment, 'id' | 'createdAt'>>): Promise<void> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, cannot update payment');
      throw new Error('Firebase not available');
    }

    try {
      const paymentRef = doc(db, PAYMENTS_REF, paymentId);
      await updateDoc(paymentRef, {
        ...updates,
        updatedAt: Date.now().toString()
      });
    } catch (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
  },

  // Delete payment
  deletePayment: async (paymentId: string): Promise<void> => {
    if (!isFirebaseAvailable()) {
      console.log('Firebase not available, cannot delete payment');
      throw new Error('Firebase not available');
    }

    try {
      const paymentRef = doc(db, PAYMENTS_REF, paymentId);
      await deleteDoc(paymentRef);
    } catch (error) {
      console.error('Error deleting payment:', error);
      throw error;
    }
  },

  // Archive payment (soft delete)
  archivePayment: async (paymentId: string): Promise<void> => {
    return paymentService.updatePayment(paymentId, { isActive: false });
  },

  // Activate payment
  activatePayment: async (paymentId: string): Promise<void> => {
    return paymentService.updatePayment(paymentId, { isActive: true });
  }
}; 